import {
  commentOf,
  responseTextOf,
  type Assessment,
  type FormControl,
  type FormElement,
  type JsonValue,
} from "@thalamos/haven-common";

type Submission = Assessment["submissions"][number];
type Response = Submission["responses"][number];

/** What an item was answered with, as a reader is shown it. */
export type ReviewedAnswer =
  | {
      kind: "rating";
      option: Extract<FormControl, { kind: "choice" }>["options"][number];
    }
  | { kind: "text"; text: string };

export type ReviewedItem = {
  item: string;
  title: string;
  answer: ReviewedAnswer;
  /** `null` where nothing was said. */
  comment: string | null;
};

export type ReviewedDomain = {
  group: string;
  title: string;
  items: ReviewedItem[];
};

export type Review = {
  domains: ReviewedDomain[];
  itemCount: number;
};

const answerOf = (
  element: Extract<FormElement, { kind: "item" }> | undefined,
  response: JsonValue,
): ReviewedAnswer => {
  const control = element?.presentation.control;
  const option =
    control?.kind === "choice"
      ? control.options.find(({ value }) => value === response)
      : undefined;

  return option === undefined
    ? { kind: "text", text: responseTextOf(response) }
    : { kind: "rating", option };
};

/**
 * A submission laid out as the form presents it: every domain the form asked,
 * answered or not, and each answered item, in the form's order.
 *
 * A comment is shown with the item it is about, where the form says which that
 * is and the item is answered. A response the form has no element for, or one
 * in a domain the form did not ask, is still shown, after the asked domains and
 * titled by its own keys where the form has no title for them, because a
 * submission is a record and nothing in it may go unseen.
 */
export const reviewOf = ({
  form,
  groupsNotAsked,
  submission,
}: {
  form: readonly FormElement[];
  /** The domains the form's rules did not ask, given the submission's answers. */
  groupsNotAsked: ReadonlySet<string>;
  submission: Submission;
}): Review => {
  const responses = new Map(
    submission.responses.map((response) => [
      `${response.group}/${response.item}`,
      response,
    ]),
  );
  const titles = new Map(
    form.flatMap((element) =>
      element.kind === "group"
        ? [[element.at.group, element.presentation.title.text] as const]
        : [],
    ),
  );
  const domains = new Map<string, ReviewedDomain>(
    [...titles]
      .filter(([group]) => !groupsNotAsked.has(group))
      .map(([group, title]) => [group, { group, title, items: [] }]),
  );
  const domainFor = (group: string): ReviewedDomain => {
    const known = domains.get(group);

    if (known !== undefined) {
      return known;
    }

    const domain: ReviewedDomain = {
      group,
      title: titles.get(group) ?? group,
      items: [],
    };
    domains.set(group, domain);
    return domain;
  };
  const reviewed = (
    response: Response,
    element?: Extract<FormElement, { kind: "item" }>,
    comment?: Response,
  ) => {
    domainFor(response.group).items.push({
      item: response.item,
      title: element?.presentation.title.text ?? response.item,
      answer: answerOf(element, response.response),
      comment: commentOf(comment),
    });
  };

  for (const element of form) {
    if (element.kind !== "item") {
      continue;
    }

    const key = `${element.at.group}/${element.at.item}`;
    const response = responses.get(key);
    const { commentAt } = element;
    const commentKey =
      commentAt === undefined
        ? undefined
        : `${commentAt.group}/${commentAt.item}`;

    if (response !== undefined) {
      reviewed(
        response,
        element,
        commentKey === undefined ? undefined : responses.get(commentKey),
      );
      responses.delete(key);

      if (commentKey !== undefined) {
        responses.delete(commentKey);
      }
    }
  }

  for (const unplaced of responses.values()) {
    reviewed(unplaced);
  }

  const reviewedDomains = [...domains.values()];

  return {
    domains: reviewedDomains,
    itemCount: reviewedDomains.reduce(
      (count, { items }) => count + items.length,
      0,
    ),
  };
};
